/**
 * Author: Prajna
 * Modified Date: 09/27/2024
 * 
 * Description:
 * The AppComponent serves as the root component of the Angular application, 
 * handling global functionalities such as navigation, theme settings, 
 * user authentication, and displaying organizational data. It acts as the main entry point to manage 
 * the overall layout, including sidebars, and listens for authentication changes 
 * to ensure proper routing based on the user's login status.
 * 
 * Key Responsibilities:
 * - Display the main UI of the application.
 * - Listen for changes in user authentication status:
 *   - Redirects to the '/node' page if logged in.
 *   - Redirects to '/login' page if not logged in.
 * - Handle dark mode settings:
 *   - Enables or disables dark mode based on localStorage settings.
 * - Manage sidebar navigation and organization selection.
 * - Provides the logout functionality and confirms the action before proceeding.
 * 
 * Usage:
 * - This component is the main parent component that governs global behaviors 
 *   and layout throughout the app.
 * - It manages the routing and appearance of the application based on user interactions 
 *   and state such as login status and theme preferences.
 */

import { RunStatusService } from './services/run-status.service';
import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { BaseComponent } from './base/base.component';
import { Organization, CompanyService } from './services/company/company.service';
import { ErrorReportingService } from './services/errorReporting/error-reporting.service';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { NavigationService } from './services/navigation/navigation.service';
import { MatDrawer } from '@angular/material/sidenav';
import { Router, RouterOutlet } from '@angular/router';
import { slideInAnimation } from 'src/animations';
import { EnvironmentService } from './services/environment/environment.service';
import { CopilotContextService, CopilotContext } from './copilot/services/copilot-context.service';
import { Location } from '@angular/common';
import { AppEnvService } from './services/app-env.service';
import { IAppEnvironment } from './model/iapp-types';
import { AuthService } from './auth.service';
import { SessionTimeoutService } from './services/session-timeout.service';
import { OrganizationService } from './views/organization/organization.service';
import { UserService } from './services/user.service';
import { OrganizationWarningDialogComponent } from './views/organization/organization-warning-dialog/organization-warning-dialog.component';
import { MatDialogConfig } from '@angular/material/dialog';
import { UserDataService } from './services/user-data-service';
import { ConfigService } from './services/config/config.service';
import { SelectOrganizationDialogComponent } from './views/organization/select-organization-dialog/select-organization-dialog.component';
import { combineLatest } from 'rxjs';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
let darkMode = localStorage.getItem("theme-mode");

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    animations: [slideInAnimation],
    standalone: false
})
export class AppComponent extends BaseComponent implements OnInit {
  /** A run is being followed on the Refinery page: navigation that would leave it is disabled. */
  runInProgress = false;

  title = 'Data Transformer';
  selectedOrganization?: Organization;
  dataSource: Organization[];
  isMenuExpanded = false;
  version: string;
  build: string;
  loginStatus: boolean = false;
  userFullName:String="";
  welcomeText:String="";
  orgname:string="";
  isDarkMode: boolean=true;
  selectedOrganizationName: string = '';
  @ViewChild("toggleElement") ref: ElementRef;
  @ViewChild('sidebarDrawer', { static: true }) sidebarDrawer: MatDrawer;
  public userdata: any;
  private config: IAppEnvironment;
  isDialogOpen = false;  // Flag to track if the dialog is open
  copilotContext: CopilotContext | null = null;

  constructor(
    private companyService: CompanyService,
    matDialog: MatDialog,
    private location: Location,
    errorLogger: ErrorReportingService,
    private navigationService: NavigationService,
    public environmentService: EnvironmentService,
    private env: AppEnvService,
    private authService: AuthService,
    private router: Router,
    private organizationService: OrganizationService,
    private sessionTimeoutService: SessionTimeoutService,
    private userService: UserService,
    private userDataService:UserDataService,
    public copilotCtx: CopilotContextService,
    private configService: ConfigService,
    private runStatus: RunStatusService,
  ) {
    super(errorLogger, matDialog);
    // While the Refinery page follows a run, navigation that would leave it is disabled;
    // Sign Out stays available (docs/Run_Job_Architecture.md).
    this.runStatus.running$.subscribe(running => this.runInProgress = running);
    const currentVersion = this.env.build();
    const savedVersion = localStorage.getItem('build');

    if (savedVersion !== currentVersion) {
      localStorage.setItem('build', currentVersion);
      //this.clearCacheAndReload();
      console.log("clear Cache And Reload As Version Changed..............")
      this.hardReload();
    }
  }

  clearCacheAndReload() {
    caches.keys().then(function(names) {
      for (let name of names) caches.delete(name);
    });
    window.location.reload();
  }
  hardReload() {
    try {
      // Check if the Cache API is available
      if ('caches' in window) {
        // Clear cache and storage
        caches.keys().then(function (names) {
          for (let name of names) caches.delete(name);
        });
      } else {
        console.warn('Cache API is not supported in this environment.');
      }
      //localStorage.clear();
      // Deliberately NOT sessionStorage.clear() — the only thing this tab
      // keeps there today is TabIdentityService's per-tab id, which the
      // refinery Edit-lock is keyed on. Wiping it here silently orphans
      // whatever lock this tab held: the next acquire request comes in
      // with a brand-new tab id, the server correctly sees that as a
      // DIFFERENT tab holding a conflicting fresh lock, and denies it —
      // which looks identical to a real conflict until the old (now
      // orphaned) lock's TTL finally expires on its own. A version-bump
      // cache-bust has no reason to touch per-tab identity at all.
      // indexedDB.databases().then((databases) => {
      //   databases.forEach((db) => indexedDB.deleteDatabase(db.name!));
      // });

      // Reload the CURRENT page, not the bare origin — window.location.href
      // = window.location.origin previously discarded wherever the Analyst
      // actually was (e.g. deep inside a node's transform-detail view) and
      // sent them through '' -> LoginGuard, which then redirects an
      // already-authenticated session straight to /node. A version bump
      // should refresh in place, not silently relocate the Analyst.
      window.location.reload();
    } catch (error) {
      console.error('Error during hard reload:', error);
    }
  }

  onSideBarOpenedChange(opened: boolean): void {
    if (!opened) {
      this.location.back();
    }
  }

  async ngOnInit(): Promise<void> {
    this.copilotCtx.context$.subscribe(ctx => this.copilotContext = ctx);

    setTimeout(() => {
      const splash = document.getElementById('splash-screen');
      if (splash) {
        splash.classList.add('hidden');
      }
    }, 2000); // Hide splash after 2 seconds
    const currentUrl = window.location.href;
    // Check if the URL contains "emailtest"
    if (currentUrl.includes('emailtest')) {
      return; // Do nothing if the URL is emailtest
    }
    if (currentUrl.includes('reset-password')) {
      return; // Do nothing if the URL is emailtest
    }
   // Check if organization data exists in localStorage and update the state
  const savedMode = localStorage.getItem('theme-mode');
  if(savedMode){
    if (savedMode === 'enabled') {
    document.body.classList.add('compact-theme');
    this.isDarkMode = true;
  } else {
    document.body.classList.remove('compact-theme');
    this.isDarkMode = false;
  }
  }else{
    this.enableDarkMode();
  }
 // const storedOrgName = localStorage.getItem('selectedOrganizationName');
 const storedOrgName = this.userDataService.getSelectedOrganizationName();
  if (storedOrgName) {
    this.selectedOrganizationName = storedOrgName;
  } else {
    this.selectedOrganizationName = ''; // Default organization name
  }
   // Subscribe to the organization name observable to get updates
   this.organizationService.selectedOrganization$.subscribe((orgName) => {
    // Clear the previous value before updating
    this.selectedOrganizationName = orgName;
    // Assuming selectedOrganizationName is already set
    if (this.selectedOrganizationName) {
      // If organization name is available, use it
      this.orgname = `Organization: ${this.selectedOrganizationName}`;
    } else {
      // If organization name is not available, use default
      this.orgname = ""  // Default organization name
    }
  });

 
   // Ask the server, authoritatively, whether this browser profile still has a
   // live session - a bookmarked/deep-linked reload never goes through
   // LoginGuard, so without this a stale "logged in" localStorage record could
   // otherwise sit uncorrected until the first API call happens to 401.
   this.authService.refresh();

   // Check login status on initialization
  this.authService.loginStatus$.subscribe(status => {
    this.loginStatus = status; // Update login status

    if (this.loginStatus) {
      const user = this.authService.getUserDetails();
    if (user) {
      this.welcomeText= `Welcome:`;
      this.userFullName = ` ${user.firstName} ${user.lastName}`;


    }
      // A resumed session (reload / deep link / restored tab) never goes through
      // warningUser() (that only runs on a "Refinery" nav-link click), so without
      // this, organizationId can sit unset all session on any page reached
      // another way — breaking every API call keyed on it (e.g. Refinery List's
      // organizationId=undefined 400). Mirrors warningUser()'s single/multi/zero-org
      // resolution, minus the navigation (a resume should stay where it is).
      this.ensureOrganizationResolved();
      this.ensureMasterConfigsResolved();
   //   this.router.navigate(['/node']); // Redirect to /node if logged in
    } else {
      this.sessionTimeoutService.stop(); // Covers logout detected via another tab or a 401, not just this tab's own button click
      // No dialog may outlive the session it was opened in (timeout warning,
      // unsaved-changes prompt, anything else) - this is the single path every
      // logout goes through, so closing them here covers all of them.
      this.matDialog.closeAll();
      this.router.navigate(['/']); // Redirect to the login page if not logged in
    }
  });
  // Subscribe to user details to update welcome text reactively
  this.authService.userDetails$.subscribe(user => {
    if (user && this.loginStatus) {
      this.welcomeText = 'Welcome:';
      this.userFullName = `${user.firstName} ${user.lastName}`;
    } else {
      this.welcomeText = '';
      this.userFullName = '';
    }
  });

    this.navigationService.setSideDrawer(this.sidebarDrawer);
    // Retrieve organizations from the service
    this.dataSource = await this.companyService.getOrganizations(1);
    if (this.dataSource.length > 0) {
      this.selectedOrganization = this.dataSource[0];
    }

    // Handle dark mode
    if (darkMode === "enabled") {
      this.enableDarkMode(); // Set state of darkMode on page load
    }
    
      this.version = this.env.version();
    this.build = this.env.build();
  this.sessionTimeoutService.onLogout.subscribe(() => {
      this.router.navigate(['/']); // Redirect to login page on logout
    });
    // Deliberately no beforeunload-triggered logout: with real multi-tab support,
    // closing one tab must not end the session for the user's other open tabs.
    // Explicit sign-out and server-side session expiry are the only ways a
    // session ends now.
  }
 
  onOrganizationChanged(organization: Organization): void {
    this.selectedOrganization = organization;
  }

  logout(): void {
    this.showConfirmDialog('Sign out Confirmation', 'Are you sure you want to Sign out?', async (confirmed) => {
      if (confirmed) {
        // AuthService.logout() already revokes the session server-side, clears
        // every auth-related localStorage key exactly once, and broadcasts the
        // change to other tabs - no need to duplicate any of that here. Awaited so
        // the navigation below runs already logged out (see SessionTimeoutService.logout).
        await this.authService.logout();
        this.sessionTimeoutService.stop();
        this.userFullName="";
        this.orgname="";
        this.selectedOrganizationName = ''; // Reset selected organization in the app state

        this.router.navigate(['/']); // Redirect to login page after logout
      }
    });
  }

  prepareRoute(outlet: RouterOutlet): void {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData.animation;
  }

  // toggleDarkTheme(): void {
  //   darkMode = localStorage.getItem("theme-mode"); // Update darkMode when clicked
  //   if (darkMode === "disabled") {
  //     this.enableDarkMode();
  //   } else {
  //     this.disableDarkMode();
  //   }
  // }

  // enableDarkMode = () => {
  //   document.body.classList.add("compact-ui");
  //   this.isDarkMode = true;
  //   localStorage.setItem("theme-mode", "enabled");
  // };

  // disableDarkMode = () => {
  //   document.body.classList.remove("compact-ui");
  //   this.isDarkMode = false;
  //   localStorage.setItem("theme-mode", "disabled");
  // };
  toggleDarkTheme(event: MatSlideToggleChange): void {
    if (event.checked === true) {
      this.enableDarkMode();
    } else {
      this.disableDarkMode();
    }
  }

enableDarkMode(): void {
  document.body.classList.add('compact-theme'); // ✅ match SCSS class name
  this.isDarkMode = true;
  localStorage.setItem('theme-mode', 'enabled');
}

disableDarkMode(): void {
  document.body.classList.remove('compact-theme');
  this.isDarkMode = false;
  localStorage.setItem('theme-mode', 'disabled');
}

  /**
   * Ensures the master transformer/node-box/farm-type config list is cached
   * for the resumed session — called from ngOnInit() on every login-status
   * resolution, same safety-net shape as ensureOrganizationResolved() right
   * below. The only other place this cache gets populated is
   * NodeDefinitionComponent.loadMasterConfigs(), which never runs for a
   * reload/deep-link/restored-tab landing directly on a transform-detail
   * route (that route doesn't mount the refinery canvas at all) — every such
   * detail component reads getMasterTransformers() with no guard, so a never-
   * populated (or since-cleared) cache crashes ngOnInit outright. Root-caused
   * 2026-10-02 after clearing a stale MASTER_TRANSFORMERS localStorage entry
   * while on a direct columnLookupTransformDetail deep link produced exactly
   * that crash ("Cannot read properties of undefined (reading 'find')") and
   * a half-initialized page. No-op if already cached (from this call or
   * NodeDefinitionComponent's own fetch, whichever ran first).
   */
  private async ensureMasterConfigsResolved(): Promise<void> {
    if (this.userDataService.getMasterTransformers()) {
      return;
    }
    try {
      const masterconfigs = await this.configService.getMasterConfigs();
      this.userDataService.setMasterNodeBoxTypes(masterconfigs.nodeboxtypes);
      this.userDataService.setMasterTransformers(masterconfigs.transformers);
      this.userDataService.setMasterFarmtypes(masterconfigs.farmtypes);
    } catch (err) {
      console.error('Failed to load master configs', err);
    }
  }

  /**
   * Ensures organizationId is populated for the resumed session, without
   * navigating anywhere — called from ngOnInit() on every login-status
   * resolution. Reuses the same single/multi/zero-org resolution warningUser()
   * already applies for the nav-click path (2026-09-20 fix), so a reload/deep
   * link/restored tab gets the same safety net a "Refinery" click already had.
   * No-op if organizationId is already set (0 counts as set — a valid,
   * deliberately-stored "no org" value, not a missing one).
   */
  private async ensureOrganizationResolved(): Promise<void> {
    const existing = this.userDataService.getSelectedOrganization();
    if (existing !== undefined && existing !== null) {
      return;
    }
    const storedUserData = this.userDataService.getOrgData();
    if (!storedUserData) {
      return; // nothing cached yet to resolve against (e.g. very first load)
    }
    const userdata = await this.userService.getUserById(storedUserData.userId);
    if (userdata.userType !== 1) {
      return; // org-resolution flow only applies to userType 1, matching warningUser()
    }
    if (userdata.organization.length === 0) {
      this.OpenWarningDialog(userdata.organization);
    } else if (userdata.organization.length > 1) {
      this.OpenEntryDialog(userdata.organization);
    } else if (userdata.organization.length === 1) {
      const organizationId = userdata.organization[0].organizationId;
      this.userDataService.setSelectedOrganization(organizationId);
      this.organizationService.setSelectedOrganization(organizationId.toString());
    }
  }

  /**
 * Handles user organization selection and navigation based on user type and associated organizations.
 * 
 * This function:
 * - Retrieves the current user's details and associated organizations.
 * - Checks the user's type (`userType`) to determine their permissions or role.
 * - Handles different scenarios for users with no organizations, a single organization, or multiple organizations:
 *    - Opens a warning dialog if no organization is associated.
 *    - Opens a selection dialog if multiple organizations exist.
 *    - Automatically selects and saves the organization if exactly one exists.
 * - Updates the selected organization in application state and `localStorage`.
 * - Navigates to the `/node` route based on the user's organization data.
 * 
 * @returns {Promise<void>} Asynchronous function that completes navigation or dialog operations.
 */

    async warningUser(): Promise<void> {
      //const user = this.authService.getUserDetails();
      const storedUserData=this.userDataService.getOrgData();
     // console.log( "storedUserData user data",storedUserData.organization);
      this.userdata = await this.userService.getUserById(storedUserData.userId);
      //console.log( "current user data",this.userdata.organization);

      if (JSON.stringify(this.userdata) !== JSON.stringify(storedUserData)) {
        //console.log("Organizations do not match, executing if block...");
        // Execute the required logic
        if(this.userdata.userType==1)
          {
            if (this.userdata.organization !== storedUserData.organization) {
    
              if (this.userdata.organization.length === 0) {
                this.orgname = '';
                this.OpenWarningDialog(this.userdata.organization);
              } else if (this.userdata.organization.length > 1) {
                this.router.navigate(['/settings']);
                this.OpenEntryDialog(this.userdata.organization);
              } else if (this.userdata.organization.length === 1) {
                  this.orgname=this.userdata.organization[0].organizationName;
                  const organizationId = this.userdata.organization[0].organizationId;
                  console.log("organizationId==",organizationId);
                 // localStorage.setItem('selectedOrganizationId', organizationId.toString());
                  //console.log("Selected Organization ID saved:", organizationId);
                  this.userDataService.setSelectedOrganization(organizationId);
                  this.organizationService.setSelectedOrganization( organizationId.toString());
                  // If exactly one organization, navigate to '/node'
                  this.router.navigate(['/node']);
                }
            }
           
          }
      } else {
      //  console.log("Organizations match, executing else block...");
        if( this.userdata.organization.length == 0)
          {
            this.OpenWarningDialog(this.userdata.organization);
          }
          else
          {
            // Bug fix 2026-09-20: this branch runs whenever the freshly-fetched
            // organization data matches the cached copy (the ordinary case on
            // every "Refinery" nav click) — but "unchanged" is not the same as
            // "already stored in THIS browser session." A single-org user whose
            // ORGANIZATIONID_ID_KEY was never populated this session (e.g. an
            // already-authenticated app resume that skipped login.component.ts's
            // unconditional set) fell through here with organizationId left
            // undefined, breaking every API call keyed on it (e.g. Refinery List's
            // organizationId=undefined 400). login.component.ts's equivalent
            // single-org path (lines ~108-114) always sets it unconditionally —
            // mirrored here so this branch does the same regardless of whether
            // the data changed.
            if (this.userdata.organization.length === 1) {
              const organizationId = this.userdata.organization[0].organizationId;
              this.userDataService.setSelectedOrganization(organizationId);
              this.organizationService.setSelectedOrganization(organizationId.toString());
            }
            this.router.navigate(['/node']);
          }
      }

     /* if(this.userdata.userType==1)
      {
        if (this.userdata.organization !== storedUserData.organization) {

          if (this.userdata.organization.length === 0) {
            this.orgname = '';
            this.OpenWarningDialog(this.userdata.organization);
          } else if (this.userdata.organization.length > 1) {
            this.router.navigate(['/settings']);
            this.OpenEntryDialog(this.userdata.organization);
          } else if (this.userdata.organization.length === 1) {
              this.orgname=this.userdata.organization[0].organizationName;
              const organizationId = this.userdata.organization[0].organizationId;
              console.log("organizationId==",organizationId);
             // localStorage.setItem('selectedOrganizationId', organizationId.toString());
              //console.log("Selected Organization ID saved:", organizationId);
              this.userDataService.setSelectedOrganization(organizationId);
              this.organizationService.setSelectedOrganization( organizationId.toString());
              // If exactly one organization, navigate to '/node'
              this.router.navigate(['/node']);
            }
        }
       
      }
      else
      {
        if( this.userdata.organization.length == 0)
          {
            this.OpenWarningDialog(this.userdata.organization);
          }
          else
          {
            this.router.navigate(['/node']);
          }
      }*/
      
    }
    OpenWarningDialog(organizations: any[]): void {
      // console.log("organozations in login",organizations)
       const dialogConfig = new MatDialogConfig();
       dialogConfig.disableClose = true;
       dialogConfig.autoFocus = true;
       dialogConfig.width = '300px';
       dialogConfig.maxWidth = '90vw';
       this.isDialogOpen = true;
       // Pass the organization data to the dialog
       dialogConfig.data = { organizations }; 
     
       const dialogRef = this.matDialog.open(OrganizationWarningDialogComponent, dialogConfig);
     
       dialogRef.afterClosed().subscribe(result => {
         if (result) {
           if (result.status === true) {
            this.userDataService.setSelectedOrganization(0);
            this.organizationService.setSelectedOrganization('0');
            localStorage.setItem('selectedOrganizationId','0');
             this.router.navigate(['/settings/organization']); // Redirect to /node if logged in
           } else if (result.status === false) {
             console.log('Organization selection failed.');
           }
         }
       });
     }
     OpenEntryDialog(organizations: any[]): void {
        // console.log("organozations in login",organizations)
         const dialogConfig = new MatDialogConfig();
         dialogConfig.disableClose = true;
         dialogConfig.autoFocus = true;
         dialogConfig.width = '300px';
         dialogConfig.maxWidth = '90vw';
         this.isDialogOpen = true;
         // Pass the organization data to the dialog
         dialogConfig.data = { organizations }; 
       
         const dialogRef = this.matDialog.open(SelectOrganizationDialogComponent, dialogConfig);
       
         dialogRef.afterClosed().subscribe(result => {
           if (result) {
             if (result.status === true) {
              this.userDataService.setSelectedOrganization(result.organizationId);
               this.router.navigate(['/node']); // Redirect to /node if logged in
             } else if (result.status === false) {
               console.log('Organization selection failed.');
             }
           }
         });
       }
}
